import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../Interface/Button";
import styled from "styled-components";
import { useMutation } from "@apollo/client";
import {
  CAPTURE_PAYMENT_PAYPAL,
  CREATE_ORDER_PAYPAL,
} from "../../query/PaypalQueries";
import { GET_ME } from "../../query/UserQueries";
import { RootContext } from "../RootContext";
import Loading from "../Loading";
import { track } from "../../analytics";

const Center = styled.div`
  text-align: center;
  margin: 0 2em;
`;

const WithdrawalConsent = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6em;
  margin: 0.8em 2em;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  line-height: 1.4;

  input[type="checkbox"] {
    margin-top: 0.1em;
    flex-shrink: 0;
    width: 1em;
    height: 1em;
    cursor: pointer;
  }
`;

function Buy({ product, onClose, onBuy }) {
  const { t } = useTranslation();
  const [error, setError] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const { setUser } = useContext(RootContext);

  const [
    createOrderPaypal,
    { loading: loadingCreateOrder, error: errorCreateOrder },
  ] = useMutation(CREATE_ORDER_PAYPAL);

  const [
    capturePaymentPaypal,
    { loading: loadingCapturePayment, error: errorCapturePayment },
  ] = useMutation(CAPTURE_PAYMENT_PAYPAL, {
    update(cache, { data: { capturePaymentPaypal } }) {
      const teeth = capturePaymentPaypal.tooth;
      const { me } = cache.readQuery({
        query: GET_ME,
      });
      const copyme = { ...me };
      copyme.tooth = teeth;
      setUser(copyme);
    },
  });

  const [{ isPending }] = usePayPalScriptReducer();

  const paypalbuttonTransactionProps = {
    style: { layout: "vertical" },
    createOrder(data, actions) {
      const order = {
        purchase_units: [
          {
            description: product.description,
            amount: {
              value: product.value,
            },
          },
        ],
      };

      track("paypal_started", { amount_eur: product.value });
      return createOrderPaypal({ variables: { price: product.value } }).then(
        () => {
          return actions.order.create(order);
        }
      );
    },
    async onApprove(data, actions) {
      setError(false);

      return capturePaymentPaypal({
        variables: { orderId: data.orderID },
      }).then(({ data }) => {
        track("paypal_completed", {
          amount_eur: product.value,
          teeth_received: data.capturePaymentPaypal.tooth,
        });
        onBuy();
      });
    },
    onCancel() {
      track("paypal_cancelled", { amount_eur: product.value });
    },
    onError(err) {
      track("paypal_error", { amount_eur: product.value });
      setError(err);
    },
  };

  return (
    <div>
      {(loadingCreateOrder || loadingCapturePayment) && <Loading />}
      {(errorCreateOrder || errorCapturePayment) && (
        <Center>{errorCreateOrder || errorCapturePayment}</Center>
      )}
      {error && <Center>{error}</Center>}
      <WithdrawalConsent>
        <input
          type="checkbox"
          checked={hasConsented}
          onChange={(e) => setHasConsented(e.target.checked)}
        />
        {t(
          "t:shop withdrawal consent",
          "__Je reconnais renoncer à mon droit de rétractation pour ce contenu numérique fourni immédiatement."
        )}
      </WithdrawalConsent>
      <Center>
        <div>
          {isPending ? (
            <h2>
              {t(
                "t:shop Load Smart Payment Button",
                "__shop Load Smart Payment Button"
              )}
            </h2>
          ) : null}
          <PayPalButtons
            {...paypalbuttonTransactionProps}
            disabled={!hasConsented}
          />
        </div>
      </Center>

      <Center>
        <Button onClick={onClose}>
          {t("t:choose another option", "__choose another option")}
        </Button>
      </Center>
    </div>
  );
}

export default Buy;
